import React, { useMemo, useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { PiPaperclip } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { EmailThread } from 'models/emailIntegration'
import { ComposeMode } from './ComposePanel'
import { EmailDealActionCallbacks } from './emailActions'
import { DealBadge } from './DealBadge'
import { InboxRowMenu } from './InboxRowMenu'

/** Format a date string: time-only for today, "Mon DD, H:MM AM" this year, "MM/DD/YY H:MM AM" older. */
function formatThreadDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const isThisYear = date.getFullYear() === now.getFullYear()
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (isToday) return time

  if (isThisYear) {
    const monthDay = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    return `${monthDay}, ${time}`
  }

  const fullDate = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })
  return `${fullDate} ${time}`
}

/** Timestamp cell with 3-dot menu that stays visible while the dropdown is open. */
function TimestampCell({
  thread,
  isSelected,
  onViewThread,
  onComposeThread,
  dealCallbacks,
  assignSubContent,
}: {
  thread: EmailThread
  isSelected: boolean
  onViewThread?: (thread: EmailThread) => void
  onComposeThread?: (thread: EmailThread, mode: ComposeMode) => void
  dealCallbacks?: EmailDealActionCallbacks
  assignSubContent?: React.ReactNode
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const date = thread.latest_message_date
  if (!date) return null

  return (
    <div className="relative flex items-center justify-end h-full">
      <span
        className={cn(
          'inbox-row-timestamp text-sm font-bold text-fg whitespace-nowrap',
          menuOpen && '!invisible'
        )}
      >
        {formatThreadDate(date)}
      </span>
      {onViewThread && onComposeThread && (
        <div
          className={cn(
            'inbox-row-menu absolute inset-y-0 right-0 flex items-center justify-end invisible',
            menuOpen && '!visible'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <InboxRowMenu
            thread={thread}
            isSelected={isSelected}
            isOpen={menuOpen}
            onView={onViewThread}
            onCompose={onComposeThread}
            assignDealSubContent={assignSubContent}
            onOpenChange={setMenuOpen}
            {...dealCallbacks}
          />
        </div>
      )}
    </div>
  )
}

interface UseInboxThreadColumnsOptions {
  onViewThread?: (thread: EmailThread) => void
  onComposeThread?: (thread: EmailThread, mode: ComposeMode) => void
  getDealCallbacks?: (thread: EmailThread) => EmailDealActionCallbacks
  getAssignDealSubContent?: (thread: EmailThread) => React.ReactNode
}

/** TanStack Table column definitions for the email thread inbox list. */
export function useInboxThreadColumns(
  options?: UseInboxThreadColumnsOptions
): ColumnDef<EmailThread, any>[] {
  const {
    onViewThread,
    onComposeThread,
    getDealCallbacks,
    getAssignDealSubContent,
  } = options ?? {}

  return useMemo(
    () => [
      {
        id: 'sender',
        accessorKey: 'latest_sender',
        header: '',
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => {
          const sender = row.original.latest_sender
          const name =
            sender?.[0]?.name || sender?.[0]?.email || 'Unknown Sender'
          const { message_count } = row.original
          return (
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-bold text-sm text-fg truncate">{name}</span>
              {message_count > 1 && (
                <span className="text-sm text-fg-secondary flex-shrink-0">
                  {message_count}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'subject_preview',
        accessorKey: 'subject',
        header: '',
        cell: ({ row }) => {
          const {
            subject,
            latest_snippet,
            has_attachments,
            deal_id,
            deal_identifier,
            deal_name,
          } = row.original
          return (
            <div className="flex items-center gap-1 min-w-0">
              {deal_id && deal_identifier && (
                <DealBadge
                  dealIdentifier={deal_identifier}
                  dealName={deal_name}
                />
              )}
              <span
                className="flex-1 whitespace-nowrap overflow-hidden text-sm min-w-0"
                style={{
                  maskImage:
                    'linear-gradient(to right, black calc(100% - 24px), transparent)',
                  WebkitMaskImage:
                    'linear-gradient(to right, black calc(100% - 24px), transparent)',
                }}
              >
                <span className="font-bold text-fg">
                  {subject || '(no subject)'}
                </span>
                {latest_snippet && (
                  <span className="text-fg-tertiary"> - {latest_snippet}</span>
                )}
              </span>
              {has_attachments && (
                <PiPaperclip className="inbox-row-attachment size-[22px] text-fg-secondary flex-shrink-0" />
              )}
            </div>
          )
        },
      },
      {
        id: 'timestamp',
        accessorKey: 'latest_message_date',
        header: '',
        size: 140,
        minSize: 140,
        maxSize: 140,
        cell: ({ row }) => (
          <TimestampCell
            thread={row.original}
            isSelected={row.getIsSelected()}
            onViewThread={onViewThread}
            onComposeThread={onComposeThread}
            dealCallbacks={getDealCallbacks?.(row.original)}
            assignSubContent={getAssignDealSubContent?.(row.original)}
          />
        ),
      },
    ],
    [onViewThread, onComposeThread, getDealCallbacks, getAssignDealSubContent]
  )
}
